<template>
  <v-container class="payment-status" style="max-width: 600px; margin: 200px auto;">
    <v-card class="status-card" elevation="10">
      <v-card-title class="status-title">
        <v-icon large :color="statusColor" class="mb-2">{{ statusIcon }}</v-icon>
        <div class="title-text">Payment Status</div>
      </v-card-title>

      <v-card-text class="status-text mt-4">
        <div v-if="status === 'COMPLETED'" class="text-center">
          <h2 class="success-text mb-4">Payment Successful!</h2>
          <v-icon x-large color="green" class="mb-4">mdi-check-circle-outline</v-icon>
          <p class="mb-4">Thank you! Your payment has been processed successfully.</p>
          <p class="mb-4">You will receive a confirmation email shortly with further instructions.</p>
          <p class="info-text mb-4">For any queries, feel free to contact our support team.</p>
        </div>

        <div v-else-if="status === 'FAILED'" class="text-center">
          <h2 class="failed-text mb-4">Payment Failed</h2>
          <v-icon x-large color="red" class="mb-4">mdi-alert-circle-outline</v-icon>
          <p class="mb-4">We apologize, but your payment could not be processed.</p>
          <p class="mb-4">Please try again or contact our support team for assistance.</p>
          <p class="info-text mb-4">Transaction ID: {{ $route.query.transaction_id || 'Not available' }}</p>
        </div>

        <div v-else-if="status === 'PENDING'" class="text-center">
          <h2 class="pending-text mb-4">Payment Processing</h2>
          <v-progress-circular
            indeterminate
            color="orange"
            size="64"
            class="mb-4"
          ></v-progress-circular>
          <p class="mb-4">Your payment is being processed. Please wait...</p>
          <p class="mb-4">This page will automatically update once completed.</p>
          <p class="info-text mb-4">Do not close this window.</p>
        </div>

        <div v-else class="text-center">
          <h2 class="error-text mb-4">Verification Error</h2>
          <v-icon x-large color="grey" class="mb-4">mdi-alert</v-icon>
          <p class="mb-4">We couldn't verify your payment status.</p>
          <p class="mb-4">Please contact our support team for assistance.</p>
          <p class="info-text mb-4">Error: Transaction ID missing or invalid</p>
        </div>

        <div class="button-group mt-6">
          <v-btn
            v-if="status === 'COMPLETED'"
            color="success"
            dark
            @click="redirectToHome"
            elevation="2"
            class="mx-2"
          >
            <v-icon left>mdi-home</v-icon>
            Go to Home
          </v-btn>

          <v-btn
            color="primary"
            href="https://api.whatsapp.com/send?phone=919618570195"
            target="_blank"
            elevation="2"
            class="mx-2"
          >
            <v-icon left>mdi-whatsapp</v-icon>
            Contact Support
          </v-btn>

          <v-btn
            color="success"
            href="https://chat.whatsapp.com/FgAlS3oIUlNEAweswvTbXc"
            target="_blank"
            elevation="2"
            class="mx-2"
          >
            <v-icon left>mdi-account-group</v-icon>
            Join Group
          </v-btn>

          <v-btn
            v-if="status === 'FAILED'"
            color="error"
            @click="retryPayment"
            elevation="2"
            class="mx-2 mt-2"
          >
            <v-icon left>mdi-refresh</v-icon>
            Retry Payment
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import axios from "axios";

export default {
  name: "PaymentStatusPage",
  data() {
    return {
      status: "PENDING",
      prod_url: "app.appgenesis.in",
      dev_url: "localhost:8000",
      url: "app.appgenesis.in",
    };
  },
  computed: {
    statusColor() {
      const colors = {
        COMPLETED: 'green',
        FAILED: 'red',
        PENDING: 'orange',
        ERROR: 'grey'
      };
      return colors[this.status] || 'grey';
    },
    statusIcon() {
      const icons = {
        COMPLETED: 'mdi-check-circle',
        FAILED: 'mdi-alert-circle',
        PENDING: 'mdi-clock-outline',
        ERROR: 'mdi-alert'
      };
      return icons[this.status] || 'mdi-alert';
    }
  },
  mounted() {
    this.verifyPaymentStatus();
    if (this.status === 'PENDING') {
      this.startPolling();
    }
  },
  methods: {
    async verifyPaymentStatus() {
      const transactionId = this.$route.query.transaction_id;
      if (!transactionId) {
        this.status = "ERROR";
        return;
      }

      try {
        const response = await axios.post(
          `https://${this.url}/api/verify-transaction/`,
          { transaction_id: transactionId }
        );
        this.status = response.data.status || "FAILED";
      } catch (error) {
        console.error("Error verifying payment:", error);
        this.status = "ERROR";
      }
    },
    startPolling() {
      // Poll every 5 seconds if status is pending
      this.pollInterval = setInterval(() => {
        if (this.status === 'PENDING') {
          this.verifyPaymentStatus();
        } else {
          clearInterval(this.pollInterval);
        }
      }, 5000);
    },
    redirectToHome() {
      window.location.href = "/";
    },
    retryPayment() {
      // Implement retry payment logic
      window.location.href = "/internship-payment";
    }
  },
  beforeDestroy() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
  }
};
</script>

<style scoped>
.payment-status {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
}

.status-card {
  border-radius: 16px;
  padding: 30px;
  width: 100%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.status-title {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
  border-bottom: 1px solid #eee;
}

.title-text {
  font-size: 28px;
  font-weight: 600;
  margin-top: 10px;
  color: #333;
}

.status-text {
  padding: 20px 0;
}

.status-text h2 {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 20px;
}

.success-text { color: #2E7D32; }
.failed-text { color: #C62828; }
.pending-text { color: #EF6C00; }
.error-text { color: #616161; }

.info-text {
  font-size: 14px;
  color: #666;
  font-style: italic;
}

.button-group {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
}

.v-btn {
  min-width: 150px;
}

@media (max-width: 600px) {
  .status-card {
    padding: 20px;
  }

  .title-text {
    font-size: 24px;
  }

  .status-text h2 {
    font-size: 20px;
  }

  .button-group {
    flex-direction: column;
    align-items: center;
  }

  .v-btn {
    width: 100%;
    margin: 5px 0;
  }
}
</style>
